import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Grid, Button, Typography } from '@mui/material';
import { initiativeGetById } from '../initiative.service';
import { appendAzureSasToken } from '../../../../../service/azure.utils';
import Loader from '../../../../components/loader';
import InitiativeIntegrators from './initiativeIntegrators';
import InitiativeAreaParticipation from './initiativeAreaParticipation';
import InitiativeSubInitiatives from './initiativeSubInitiatives';
import { integratorTypeLabel } from '../../../ecosystemPartners/integratorType';
import { sanitizeHtml } from '../../../../../helper/sanitizeHtml';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../../styles/detailStyles';

const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

function DetailField({ label, value, fullWidth = false, html = false, link = false, children, minHeight }) {
  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>
          {label}
        </Typography>
        {children ? (
          children
        ) : html ? (
          <Box
            sx={{
              ...detailValueSx,
              '& p': { m: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
          />
        ) : link && value ? (
          <Box
            component="a"
            href={value}
            target="_blank"
            rel="noreferrer"
            sx={{
              ...detailValueSx,
              color: '#294190',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography sx={detailValueSx}>
            {value || '-'}
          </Typography>
        )}
      </Box>
    </Grid>
  );
}

const InitiativeView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initiativeData, setInitiativeData] = useState({});

  const getInitiativeData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await initiativeGetById(id);
      const transformedData = {
        ...data,
        countryName: (Array.isArray(data?.adminData?.countries) && data.adminData.countries.length > 0
          ? data.adminData.countries.map((c) => c.name).join(', ')
          : data?.creatorData?.country) || '-',
        sector: data?.adminData?.sector,
        initiativeLink: data?.adminData?.initiativeLink || data?.creatorData?.initiativeLink,
        imageUrl: data?.adminData?.imageUrl || data?.creatorData?.logoUrl,
        allowedIntegratorTypes: Array.isArray(data?.allowedIntegratorCategories) && data.allowedIntegratorCategories.length > 0
          ? data.allowedIntegratorCategories.map(integratorTypeLabel).join(', ')
          : 'All integrator types',
      };
      setInitiativeData(transformedData || {});
    } catch (error) {
      console.error('Error fetching initiative by ID:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getInitiativeData();
  }, [getInitiativeData]);

  const imageUrl = appendAzureSasToken(initiativeData?.imageUrl) || '';

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1360, xl: 1280 }, boxSizing: 'border-box', mx: 'auto', px: { xs: 0, lg: 3, xl: 4 } }}>
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/initiative')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': {
                fontSize: 18,
              },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            Initiative Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          <Button
            onClick={() => navigate(`/addInitiative/${id}?type=edit`)}
            startIcon={<span className="material-symbols-rounded">edit</span>}
            sx={{
              minWidth: { xs: '100%', sm: 86 },
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#DFE5F6',
              color: '#294190',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#DFE5F6',
              },
              '& .material-symbols-rounded': {
                fontSize: 18,
              },
            }}
          >
            Edit
          </Button>
        </Grid>
      </Grid>

      <Box sx={pageCardSx}>
        <Typography sx={detailSectionTitleSx}>
          Initiative Details
        </Typography>

        <Grid container spacing={2.25}>
          <DetailField label="Initiative Name" value={initiativeData?.name} />
          <DetailField label="Country" value={initiativeData?.countryName} />
          <DetailField label="Initiative Link" value={initiativeData?.initiativeLink} link />
          <DetailField label="Sector" value={initiativeData?.sector} />
          <DetailField label="Integrator Types Allowed" value={initiativeData?.allowedIntegratorTypes} />
          <DetailField label="Description" value={initiativeData?.description} html fullWidth minHeight={128} />
          <DetailField label="Image" fullWidth minHeight={imageUrl ? 260 : 96}>
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={initiativeData?.name || 'Initiative'}
                sx={{
                  width: '100%',
                  maxHeight: { xs: 220, sm: 260, md: 320 },
                  objectFit: 'contain',
                  borderRadius: '12px',
                  backgroundColor: '#FEFEFE',
                }}
                onError={(event) => {
                  event.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontSize: 16,
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  color: '#1C1B1F',
                }}
              >
                -
              </Typography>
            )}
          </DetailField>
        </Grid>
      </Box>

      <InitiativeIntegrators initiativeId={id} />

      <InitiativeAreaParticipation initiativeId={id} />

      <InitiativeSubInitiatives initiativeId={id} countries={initiativeData?.adminData?.countries || []} />

      {loading && <Loader />}
    </Box>
  );
};

export default InitiativeView;
